import * as React from 'react';
import { connect } from 'react-redux';
import * as _ from 'underscore';

import {
  InlineEditorLogic,
  isEditMode,
  MergeOrUpdateStateAction,
  useInlineEditor
} from '@tableau/vizportalclient-common-components';
import {
  CheckBoxWithLabelWidget,
  DropdownButtonType,
  List,
  ListItem,
  RadioButtonWithLabelGroupWidget,
  SingleSelectDropdown,
  TabStyles,
  ToastType
} from '@tableau/widgets';

import { ACTION_DIALOG_ID } from '~src/app/content/contentDetailDialog/ContentDetailsDialog';
import {
  ContentDetailSection,
  DetailEditButton,
  DetailSaveCancelButtons
} from '~src/app/content/contentDetailDialog/ContentDetailSection';
import { selectSessionSite, selectSessionUser } from '~src/app/selectors/SessionInfoSelectors';
import { selectSpaceInfoItem } from '~src/app/selectors/SpaceInfoSelectors';
import { AppState } from '~src/app/state/AppState';
import { MapStateToProps } from '~src/app/utils/ConnectUtils';
import { Messages } from '~src/codegen/Localize';
import { IResult } from '~src/httpClient/serverApi/types/IResponse';
import { rejectIfResultContainsError } from '~src/httpClient/utils/HttpErrorChecker';
import { AskDataMode } from '~src/settings/types/AskDataMode';
import { FeatureFlags } from '~src/ts/FeatureFlags';
import { IDataSourceInfo, NlpSetting } from '~src/ts/models/IDataSourceInfo';
import { ToasterService } from '~src/ts/services/ToasterService';


// ID for accessibilty purposes
const sectionHeaderId = ACTION_DIALOG_ID+'-AskData';

interface LegacyAskDataSettingsSectionOwnProps {
  readonly isAdminOrOwner: boolean;
  readonly setNlpSettings: (nlpOption: NlpSetting, feedbackSetting: boolean) => Promise<IResult>;
  readonly incrementRouterSpaceReactKey: () => void;
}

interface LegacyAskDataSettingsSectionStateProps {
  readonly askDataMode: AskDataMode;
  readonly canAuthorOnWeb: boolean;
  readonly nlpSetting: NlpSetting;
  readonly feedbackToCurator: boolean;
}

export type LegacyAskDataSettingsProps =
    LegacyAskDataSettingsSectionOwnProps &
    LegacyAskDataSettingsSectionStateProps;

export interface LegacyAskDataSettingsEditorState {
  nlpSetting: NlpSetting;
  indexingSetting: NlpSetting;
  feedbackToCurator: boolean;
}

export function ShowLegacyAskDataSectionIfAvailable(props: LegacyAskDataSettingsProps): JSX.Element {

  return (
    <>
      {
        props.askDataMode !== AskDataMode.DISABLED_ALWAYS &&
        <LegacyAskDataSettingsSection {...props} />
      }
    </>
  );
}


export function LegacyAskDataSettingsSection(props: LegacyAskDataSettingsProps): JSX.Element {

  // remove webAuthoringViewerEnablementPermission, when nlpviewerEnablementEnabled is removed
  const webAuthoringViewerEnablementPermission: boolean =
    FeatureFlags.nlpviewerEnablementEnabled ? true : props.canAuthorOnWeb;

  // remove canEdit and replace with props.isAdminOrOnwer when nlpviewerEnablementEnabled is removed
  const canEdit = webAuthoringViewerEnablementPermission && props.isAdminOrOwner;

  const onSave = React.useCallback(async (editorState: LegacyAskDataSettingsEditorState) => {
    let nlpSetting = editorState.nlpSetting;
    if (nlpSetting === NlpSetting.enabled) {
      nlpSetting = editorState.indexingSetting;
    }

    await props.setNlpSettings(nlpSetting, editorState.feedbackToCurator)
      .then(response => {
        if (response.errors && response.errors.length) { // unexpected server error
          ToasterService.instance.pop({ type: ToastType.Error, text: Messages.dataSourceNlpSetting_failed() });
        }
        return response;
      })
      .then(rejectIfResultContainsError)
      .then(props.incrementRouterSpaceReactKey);
  },[
    props.setNlpSettings,
    props.incrementRouterSpaceReactKey
  ]);

  const canSave = (editorState: LegacyAskDataSettingsEditorState) => {
    return !_.isEqual(editorState, initialState);
  };

  const initialState = getInitialState(props.nlpSetting, props.feedbackToCurator);

  const logic: InlineEditorLogic<LegacyAskDataSettingsEditorState> = useInlineEditor<LegacyAskDataSettingsEditorState>({
    editorStateWhenEnteringEditMode: initialState,
    canEdit: canEdit,
    onSave: onSave,
    canSaveFn: canSave,
  });


  if (isEditMode(logic)) {
    return (
      <ContentDetailSection
        header={Messages.nlp()}
        testId={'ask-data'}
        headerId={sectionHeaderId}
        detail={
          <AskDataEditMode
            askDataMode={props.askDataMode}
            nlpSetting={logic.editorState.nlpSetting}
            indexingSetting={logic.editorState.indexingSetting}
            feedbackToCurator={logic.editorState.feedbackToCurator}
            setEditorState={logic.setEditorStateWithinEditMode}
            disableControls={logic.isActionBusy}
          />
        }
        isActionAllowed={true}
        action={
          <DetailSaveCancelButtons
            inlineEditorLogic={logic}
          />
        }
      />
    );
  }
  else {
    return (
      <ContentDetailSection
        header={Messages.nlp()}
        testId={'ask-data'}
        detail={
          <AskDataReadingMode
            webAuthoringViewerEnablementPermission={webAuthoringViewerEnablementPermission}
            nlpSetting={props.nlpSetting}
            askDataMode={props.askDataMode}
          />
        }
        isActionAllowed={logic.canEdit}
        action={
          <DetailEditButton
            buttonLabel={Messages.dataSourceNlpSetting_editNlpSettingLabel()}
            buttonTestId={'edit-ask-data'}
            onClickAction={logic.enterEditMode}
          />
        }
      />
    );
  }
}

function getInitialState(nlpSetting: NlpSetting, feedbackToCurator: boolean): LegacyAskDataSettingsEditorState {

  const initialState: LegacyAskDataSettingsEditorState = {
    nlpSetting: nlpSetting,
    indexingSetting: NlpSetting.enabled,
    feedbackToCurator: feedbackToCurator
  };

  if(initialState.nlpSetting === NlpSetting.refresh24Hrs || initialState.nlpSetting === NlpSetting.disableIndex) {
    initialState.nlpSetting = NlpSetting.enabled;
    initialState.indexingSetting = nlpSetting;
  }

  return initialState;
}


export interface AskDataReadingModeProps {
  readonly webAuthoringViewerEnablementPermission: boolean;
  readonly nlpSetting: NlpSetting;
  readonly askDataMode: AskDataMode;
}

export function AskDataReadingMode(props: AskDataReadingModeProps): JSX.Element {
  const getDetailReadingModeMessage= (props: AskDataReadingModeProps) => {
    // remove this block, when nlpviewerEnablementEnabled is removed
    if (!props.webAuthoringViewerEnablementPermission) {
      return Messages.dataSourceNlpSetting_error_state_NonAuthoring();
    }
    switch (props.nlpSetting) {
      case NlpSetting.disabled:
        return Messages.dataSourceNlpSetting_disabled();
      case NlpSetting.siteDefault:
        if (props.askDataMode === AskDataMode.ENABLED_BY_DEFAULT) {
          return Messages.dataSourceNlpSetting_siteDefault_enabled();
        }
        if (props.askDataMode === AskDataMode.DISABLED_BY_DEFAULT) {
          return Messages.dataSourceNlpSetting_siteDefault_disabled();
        }
      case NlpSetting.enabled:
        return Messages.dataSourceNlpSetting_enabled();
      case NlpSetting.disableIndex:
        return Messages.dataSourceNlpSetting_indexDisabled();
      case NlpSetting.refresh24Hrs:
        return Messages.dataSourceNlpSetting_timedRefresh();
      default:
        return '';
    }
  };

  return (
    <span>
      {getDetailReadingModeMessage(props)}
    </span>
  );
}

export interface AskDataEditModeProps {
  readonly askDataMode: AskDataMode;
  readonly nlpSetting: NlpSetting;
  readonly indexingSetting: NlpSetting;
  readonly feedbackToCurator: boolean;
  readonly setEditorState: React.Dispatch<MergeOrUpdateStateAction<LegacyAskDataSettingsEditorState>>;
  readonly disableControls: boolean;
}

export function AskDataEditMode(props: AskDataEditModeProps): JSX.Element {

  const isSiteDefaultEnabled = props.nlpSetting === NlpSetting.siteDefault
    && props.askDataMode === AskDataMode.ENABLED_BY_DEFAULT;
  const showFeedbackToCurator = FeatureFlags.nlFeedbackToCuratorEnabled
    && (props.nlpSetting === NlpSetting.enabled || isSiteDefaultEnabled);

  return (
    <>
      <NLPEnablementComponent
        askDataMode={props.askDataMode}
        nlpSetting={props.nlpSetting}
        setEditorState={props.setEditorState}
        disableDropdown={props.disableControls}
      />
      {isSiteDefaultEnabled &&
        <div style={{paddingBottom: TabStyles.Spacing.DefaultSpace}} data-tb-test-id={'site-default-message'}>
          {Messages.dataSourceNlpSetting_option_siteDefault_message()}
        </div>
      }
      {props.nlpSetting === NlpSetting.enabled &&
        <NLPIndexingComponent
          indexingSetting={props.indexingSetting}
          setEditorState={props.setEditorState}
          disableButtons={props.disableControls}
        />
      }
      {showFeedbackToCurator &&
        <FeedbackComponent
          feedbackToCurator={props.feedbackToCurator}
          setEditorState={props.setEditorState}
          disableCheckbox={props.disableControls}
        />
      }
    </>
  );
}

interface NLPEnablementProps {
  readonly askDataMode: AskDataMode;
  readonly nlpSetting: NlpSetting;
  readonly setEditorState: React.Dispatch<MergeOrUpdateStateAction<LegacyAskDataSettingsEditorState>>;
  readonly disableDropdown: boolean;
}

export function NLPEnablementComponent(props: NLPEnablementProps): JSX.Element {
  const settingItems: ListItem[] = [
    {
      value: NlpSetting.enabled,
      content: Messages.dataSourceNlpSetting_option_enabled(),
    },
    {
      value: NlpSetting.disabled,
      content: Messages.dataSourceNlpSetting_option_disabled(),
    },
    {
      value: NlpSetting.siteDefault,
      content: props.askDataMode === AskDataMode.ENABLED_BY_DEFAULT ?
        Messages.dataSourceNlpSetting_option_siteDefault_enabled() :
        Messages.dataSourceNlpSetting_option_siteDefault_disabled(),
    },
  ];

  return (
    <SingleSelectDropdown
      style={{
        // The longest item requires 285px width (Spanish language)
        width: 285,
        marginBottom: TabStyles.Spacing.DefaultSpace,
      }}
      label=""
      testId="nlp-enablement-component"
      aria-labelledby={sectionHeaderId}
      list={<List items={settingItems} />}
      type={DropdownButtonType.Outline}
      onChange={value => props.setEditorState({nlpSetting: value as NlpSetting})}
      value={props.nlpSetting}
      disabled={props.disableDropdown}
    />
  );
}

interface NLPIndexingProps {
  readonly indexingSetting: NlpSetting;
  readonly setEditorState: React.Dispatch<MergeOrUpdateStateAction<LegacyAskDataSettingsEditorState>>;
  readonly disableButtons: boolean;
}

export function NLPIndexingComponent(props: NLPIndexingProps): JSX.Element {
  const radioButtonList = [
    {
      label: Messages.dataSourceNlpSetting_indexing_option_automatic(),
      testId: 'automatic',
      value: NlpSetting.refresh24Hrs,
      disabled: props.disableButtons,
    },
    {
      label: Messages.dataSourceNlpSetting_indexing_option_manual(),
      testId: 'manual',
      value: NlpSetting.enabled,
      disabled: props.disableButtons,
    },
    {
      label: Messages.dataSourceNlpSetting_indexing_option_disabled(),
      testId: 'disabled',
      value: NlpSetting.disableIndex,
      disabled: props.disableButtons,
    },
  ];

  return (
    <div style={{paddingBottom: TabStyles.Spacing.DefaultSpace}}>
      <Title>{Messages.dataSourceNlpSetting_option_Title()}</Title>
      <RadioButtonWithLabelGroupWidget
        testId="nlp-indexing"
        ariaLabel={Messages.dataSourceNlpSetting_option_Title()}
        buttonList={radioButtonList}
        checkedIndex={radioButtonList.findIndex(item => item.value === props.indexingSetting)}
        handleChange={value => props.setEditorState({indexingSetting: value})}
      />
    </div>
  );
}

interface FeedbackProps {
  readonly feedbackToCurator: boolean;
  readonly setEditorState: React.Dispatch<MergeOrUpdateStateAction<LegacyAskDataSettingsEditorState>>;
  readonly disableCheckbox: boolean;
}

export function FeedbackComponent(props: FeedbackProps): JSX.Element {
  return (
    <div>
      <Title>{Messages.dataSourceNlpSetting_feedbackToCuratorTitle()}</Title>
      <CheckBoxWithLabelWidget
        label={Messages.dataSourceNlpSetting_feedbackToCuratorLabel()}
        testId={'feedback-to-curator'}
        checked={props.feedbackToCurator}
        handleChange={checked => props.setEditorState({feedbackToCurator: checked})}
        disabled={props.disableCheckbox}
      />
    </div>
  );
}

const Title: React.FC = ({children}) => (
  <div style={{
    color: TabStyles.Typography.Color.Secondary,
    fontSize: TabStyles.Typography.Sizing.Large,
    paddingBottom: TabStyles.Spacing.DefaultSpace,
  }}>
    {children}
  </div>
);


const mapStateToProps: MapStateToProps<LegacyAskDataSettingsSectionStateProps> = (state: AppState) => ({
  askDataMode: selectSessionSite(state).askDataMode,
  // remove access of canAuthorOnWeb when nlpviewerEnablementEnabled is removed
  canAuthorOnWeb: selectSessionUser(state).canAuthorOnWeb,
  nlpSetting: (selectSpaceInfoItem(state) as IDataSourceInfo).nlpSetting,
  feedbackToCurator: (selectSpaceInfoItem(state) as IDataSourceInfo).askDataFeedbackToCuratorEnabled,
});

export const ConnectedLegacyAskDataSettingsSection: React.ComponentClass<LegacyAskDataSettingsSectionOwnProps> =
  connect(mapStateToProps)(ShowLegacyAskDataSectionIfAvailable);

